import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import { roundAtSecondDecimal } from './numbers';
import { RentCellStatus, RentSortField, RentValueMode, SortDirection, type RentCell, type RentSort } from './types';

type RentCellAmounts = Pick<RentCell, 'due' | 'forecast' | 'paid'>;
type RentCellState = Pick<RentCell, 'forecast' | 'status'>;
type RentCellFigures = Pick<RentCell, 'due' | 'forecast' | 'paid' | 'status'>;

const ACTIONABLE_STATUSES: RentCellStatus[] = [RentCellStatus.LATE, RentCellStatus.PARTIAL, RentCellStatus.PAID];
const STATUSES_WITHOUT_AMOUNT: RentCellStatus[] = [
  RentCellStatus.COVERED,
  RentCellStatus.EMPTY,
  RentCellStatus.NONE,
  RentCellStatus.VACANT,
];
const ASCENDING_BY_DEFAULT_SORT_FIELDS: RentSortField[] = [
  RentSortField.LEASE_REF,
  RentSortField.TENANT_UNIT,
  RentSortField.UNIT,
];
const OPPOSITE_DIRECTION: Record<SortDirection, SortDirection> = {
  [SortDirection.ASC]: SortDirection.DESC,
  [SortDirection.DESC]: SortDirection.ASC,
};

const OWNER_GROUP_KEY_SEPARATOR = '+';

export const DEFAULT_RENT_SORT: RentSort = { direction: SortDirection.DESC, field: RentSortField.UNPAID_FIRST };

export const countsInRentTotals = (cell: Pick<RentCell, 'forecast'>, mode: RentValueMode): boolean =>
  !cell.forecast || mode === RentValueMode.EXPECTED;

export const rentCellValue = (cell: RentCellAmounts, mode: RentValueMode): number => {
  if (!countsInRentTotals(cell, mode)) {
    return 0;
  }
  switch (mode) {
    case RentValueMode.EXPECTED:
      return cell.due;
    case RentValueMode.OUTSTANDING:
      return roundAtSecondDecimal(Math.max(0, cell.due - cell.paid));
    case RentValueMode.COLLECTED:
    default:
      return Math.min(cell.paid, cell.due);
  }
};

export const hasRentCellAmount = (status: RentCellStatus): boolean => !STATUSES_WITHOUT_AMOUNT.includes(status);

export const rentCellSortValue = (cell: RentCellAmounts, mode: RentValueMode): number =>
  cell.forecast ? cell.due : rentCellValue(cell, mode);

/** What a cell shows: its forecast amount before the rent call, nothing for a month without rent. */
export const rentCellDisplayValue = (cell: RentCellFigures, mode: RentValueMode): number | null =>
  hasRentCellAmount(cell.status) ? rentCellSortValue(cell, mode) : null;

export const isRentCellCalledNotDue = (cell: RentCellState): boolean =>
  cell.status === RentCellStatus.FUTURE && !cell.forecast;

export const isRentCellActionable = (cell: RentCellState): boolean =>
  ACTIONABLE_STATUSES.includes(cell.status) || isRentCellCalledNotDue(cell);

export const getDefaultRentSortDirection = (field: RentSortField): SortDirection =>
  ASCENDING_BY_DEFAULT_SORT_FIELDS.includes(field) ? SortDirection.ASC : SortDirection.DESC;

export const getRentSortDirection = (sort: RentSort): SortDirection =>
  sort.direction ?? getDefaultRentSortDirection(sort.field);

export const isActiveRentSort = (sort: RentSort, field: RentSortField, month?: number | null): boolean =>
  sort.field === field && (field !== RentSortField.MONTH || sort.month === month);

export const toggleRentSort = (sort: RentSort, field: RentSortField, month?: number | null): RentSort => ({
  direction: isActiveRentSort(sort, field, month)
    ? OPPOSITE_DIRECTION[getRentSortDirection(sort)]
    : getDefaultRentSortDirection(field),
  field,
  month,
});

export const getRentOwnerGroupKey = (ownerIds: string[]): string | null =>
  isEmpty(ownerIds) ? null : sortBy(uniq(ownerIds)).join(OWNER_GROUP_KEY_SEPARATOR);
